"use client";

import { Core } from "@faire/design-tokens";
import { SignUpRequestExpanded } from "@faire/retailer-visitor-shared/features/SignUp/SignUpRequestData";
import { isCountryUSA } from "@faire/retailer-visitor-shared/lib/countryUtils";
import { SignupFormContainer } from "@faire/retailer-visitor-shared/views/SignUp/_components/CommonStyles";
import { SignUpTitle } from "@faire/retailer-visitor-shared/views/SignUp/_components/SignUpTitle";
import { useIncomeRanges } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useIncomeRanges";
import { useSignUpUPLT } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpUPLT";
import {
  AnnualSalesData,
  AnnualSalesForm,
  useSchema,
} from "@faire/retailer-visitor-shared/views/SignUp/annual-sales/__internal__/AnnualSalesForm";
import { useSignUpRequest } from "@faire/retailer-visitor-shared/views/SignUp/SignUpRequestContext";
import { useSaveRequestAndGoToNextStep } from "@faire/retailer-visitor-shared/views/SignUp/signupStepper";
import { Step } from "@faire/retailer-visitor-shared/views/SignUp/Step";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { ISignUpRequest } from "@faire/web-api--source/indigofair/data/ISignUpRequest";
import privacyPagePath from "@faire/web-api--source/routes/www/privacy";
import { Formik } from "formik";
import React, { useMemo } from "react";

export const AnnualSalesStep: React.FC = () => {
  const { strictLocalize } = useStrictLocalization();
  const { saveAndGoToNextStep } = useSaveRequestAndGoToNextStep(
    Step.ANNUAL_SALES
  );
  const [{ country, annual_sales, reported_creation_time }] = useSignUpRequest([
    "country",
    "annual_sales",
    "reported_creation_time",
  ]);
  const { incomeRangeForKey, incomeRangeByDisplayText, isLoadingIncomeRanges } =
    useIncomeRanges(country);
  useSignUpUPLT({ step: Step.ANNUAL_SALES, isLoading: isLoadingIncomeRanges });

  const isGlobal = !isCountryUSA(country);
  const schema = useSchema();
  const initialValues = useMemo(
    () => ({
      annualSales: incomeRangeByDisplayText(
        annual_sales ?? ISignUpRequest.IAnnualSales.build({})
      )?.display_text,
    }),
    [annual_sales, incomeRangeByDisplayText]
  );

  const isLessThan12Months =
    reported_creation_time === "STARTED_THIS_YEAR" ||
    reported_creation_time === "STARTED_ONE_YEAR_AGO";

  const onNext = async (values: AnnualSalesData) => {
    const incomeRange = incomeRangeForKey(values.annualSales ?? "");

    const payload: Partial<SignUpRequestExpanded> = {
      annual_sales: values.annualSales
        ? ISignUpRequest.IAnnualSales.build({
            ...incomeRange,
          })
        : undefined,
    };

    saveAndGoToNextStep(payload);
  };

  return (
    <SignupFormContainer>
      <SignUpTitle
        title={
          isLessThan12Months
            ? strictLocalize({
                defaultMessage: "What are your estimated annual sales?",
                description: {
                  img: "https://cdn.faire.com/fastly/4bd1746e20b5f02cc6244746bade47fdf4abc47dc7945f600245201d1a4dd0f6.png",
                  text: "Title of the annual sales step if store is less than 12 months old",
                },
              })
            : strictLocalize({
                defaultMessage: "What are your annual sales?",
                description: {
                  img: "https://cdn.faire.com/fastly/d3b7eac867033dca8f512db2ccea403d69347c550d540f701503e1e896032bf1.png",
                  text: "Title of the annual sales step",
                },
              })
        }
      />
      <Spacer height="4x" />
      <Formik
        initialValues={initialValues}
        validationSchema={schema}
        onSubmit={onNext}
        enableReinitialize
      >
        <AnnualSalesForm />
      </Formik>
      {isGlobal ? (
        <>
          <Spacer height="3x" />
          <Typography variant="labelSansRegular" color={Core.text.subdued}>
            <StrictLocalMsg
              defaultMessage="We will not share this information. Learn more about how we use and protect your data at Faire. <LinkFormatting>Privacy at Faire</LinkFormatting>"
              description={{
                text: "Privacy policy disclaimer",
                img: "https://cdn.faire.com/fastly/d590903f1f67e42499c799b8ac7b4665aefb444438833851e24f149776724cf8.png",
              }}
              values={{
                LinkFormatting: PrivacyPathLink,
              }}
            />
          </Typography>
        </>
      ) : null}
    </SignupFormContainer>
  );
};

const PrivacyPathLink = (chunks: React.ReactNode) => (
  <Button
    variant="tertiary"
    target="_blank"
    style={{ color: "inherit", fontSize: "inherit" }}
    linkTo={privacyPagePath()}
  >
    {chunks}
  </Button>
);
